import React, { useState } from 'react';
import { Button } from 'antd';
import { useNavigate, useLocation } from 'react-router';
import { CheckCircleOutlined, CreditCardOutlined } from '@ant-design/icons';
import axios from 'axios';
import { toast } from 'sonner';
import logo from '@/assets/images/vantageAI.png';
import valiantlytixLogo from '@/assets/images/darkLogo.png';
import loginBackground from '@/assets/images/login_background.png';

const EnterprisePaymentPromptPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location.state?.userId;
  const [loading, setLoading] = useState(false);
  const { VITE_APP_URL: APP_URL } = import.meta.env;

  const handleProceedPayment = async () => {
    if (!userId) {
      toast.error("User ID not found. Please try logging in instead.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${APP_URL}/api/payments/stripe/enterprise-subscription`, {
        user_id: userId
      });
      
      if (res.data?.payment_link_url) {
        window.location.href = res.data.payment_link_url;
      } else {
        toast.error("Could not generate payment link.");
        setLoading(false);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || err.response?.data?.error || "An error occurred while generating the payment link.");
      setLoading(false);
    }
  };

  return (
    <div className='flex w-full h-screen bg-[#F8F9FA] font-sans'>
      {/* Left side: Content Panel */}
      <div className='w-full lg:w-1/2 h-full flex flex-col items-center relative px-4 sm:px-8 overflow-y-auto'>
        
        <div className='w-full max-w-[440px] flex flex-col items-center my-auto py-8'>
          {/* Logo at Top */}
          <div className='w-full flex justify-center mb-8'>
            <img src={logo} alt="Vantage AI Logo" className='w-[160px] md:w-[200px] h-auto object-contain' />
          </div>

          {/* Payment Prompt Container */}
          <div className='w-full bg-white px-6 sm:px-8 py-10 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center'>
            
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
              <CheckCircleOutlined className="text-3xl text-green-600" />
            </div>

            <h2 className='text-2xl font-bold text-gray-900 mb-3'>Account Created Successfully!</h2>
            <p className='text-gray-500 mb-8 leading-relaxed'>
              Your enterprise account has been set up. To unlock all enterprise features and complete your onboarding, please proceed to payment.
            </p>

            <div className='w-full flex flex-col gap-4'>
              <Button 
                type='primary' 
                size='large' 
                loading={loading}
                icon={!loading && <CreditCardOutlined />}
                onClick={handleProceedPayment} 
                className='w-full h-12 text-base font-medium !bg-[#A91D22] hover:!bg-[#8a171b] !border-none rounded-lg shadow-md transition-all flex items-center justify-center'
              >
                Proceed to Payment
              </Button>
              
              {/* <Button 
                type='default' 
                size='large' 
                onClick={() => navigate("/login")} 
                className='w-full h-12 text-base font-medium text-gray-600 border-gray-300 hover:text-gray-800 hover:border-gray-400 rounded-lg transition-all flex items-center justify-center'
              >
                Return to Login
              </Button> */}
            </div>
            
          </div>
        </div>

        <span className='text-gray-400 text-xs md:text-sm select-none pb-6 mt-auto'>&copy; 2026 Vantage AI. All rights reserved.</span>
      </div>

      {/* Right side: Image Panel */}
      <div className='hidden lg:flex w-1/2 relative bg-black items-center justify-center overflow-hidden'>
        <img src={loginBackground} alt="Vantage AI Dashboard Graphic" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        
        <div className="absolute top-16 left-16 z-10">
          <div className="text-white text-[42px] leading-[1.2] font-bold tracking-tight">
            <div>Unlock powerful</div>
            <div><span className="text-[#FCA5A5]">Enterprise Features</span></div>
            <div>for your team.</div>
          </div>
        </div>

        {/* Valiantlytix Logo Overlay */}
        <div className="absolute bottom-12 right-12 z-10">
          <img src={valiantlytixLogo} alt="Valiantlytix Logo" className="w-[180px] h-auto object-contain" />
        </div>
      </div>
    </div>
  );
};

export default EnterprisePaymentPromptPage;
